/* Kudo popups — show/dismiss lifecycle + form submit.
 *
 * Config comes from wp_localize_script('kudoPopup', ...):
 *   id, delay (s), redisplayDays, preview, showAfterPurchase, ajaxUrl,
 *   action, nonce, i18nInvalid, i18nError, successCloseMs
 *
 * Dismissal state — localStorage only, key "kudo_popup_<id>":
 *   { hideUntil: <ms epoch> }
 *   localStorage has no native TTL, so the expiry lives in the value and is
 *   checked on read. Closed without submitting -> hidden for `redisplayDays`.
 *   Submitted -> hidden for ~10 years (effectively permanent).
 *
 * Purchased flag — localStorage key PURCHASED_KEY below, written by
 * Kudotrade_Popups_Frontend::suppress_after_purchase() (class-popups-frontend.php)
 * on the WooCommerce thank-you page. Generic and popup-id-agnostic: once set,
 * EVERY popup — including ones created after the purchase — stays hidden for
 * that visitor. Not read anywhere else; this file is the single source of
 * truth for what the flag means.
 *
 * The markup is printed late in wp_footer, so everything waits for
 * DOMContentLoaded before touching the DOM.
 */
(function () {
    'use strict';

    var DAY = 864e5;
    var PURCHASED_KEY = 'kudo_popup_purchased';

    function hasPurchased() {
        try {
            return !!window.localStorage.getItem(PURCHASED_KEY);
        } catch (e) {
            return false;
        }
    }

    function init() {
        var cfg = window.kudoPopup;
        if (!cfg || !cfg.id) return;

        var root = document.getElementById('kudo-popup-' + cfg.id);
        if (!root) return;

        var KEY     = 'kudo_popup_' + cfg.id;
        var days    = Math.max(1, parseInt(cfg.redisplayDays, 10) || 1);
        var preview = !!cfg.preview;

        // --- dismissal state (localStorage, self-expiring) ------------
        function readHideUntil() {
            try {
                var raw = window.localStorage.getItem(KEY);
                if (!raw) return 0;
                var parsed = JSON.parse(raw);
                return (parsed && parseInt(parsed.hideUntil, 10)) || 0;
            } catch (e) {
                return 0;
            }
        }
        function hideFor(ms) {
            try {
                window.localStorage.setItem(KEY, JSON.stringify({ hideUntil: Date.now() + ms }));
            } catch (e) {}
        }
        function shouldShow() {
            var until = readHideUntil();
            if (!until) return true;
            if (Date.now() >= until) {
                try { window.localStorage.removeItem(KEY); } catch (e) {}
                return true;
            }
            return false;
        }

        // Admin preview (?popup_preview=<id>) ignores the purchased flag and
        // the frequency cap, and never persists a dismissal, so it can be
        // reopened freely. A popup with "show_after_purchase" enabled opts
        // itself out of the purchased-flag check specifically (e.g. a
        // post-purchase upsell) but still respects its own frequency cap.
        var ignorePurchased = !!cfg.showAfterPurchase;
        if (!preview && ((hasPurchased() && !ignorePurchased) || !shouldShow())) return;

        // --- focus management ---------------------------------------
        var dialog = root.querySelector('[role="dialog"]') || root;
        var lastFocused = null;
        var FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]),' +
            'select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

        function focusables() {
            return Array.prototype.filter.call(
                dialog.querySelectorAll(FOCUSABLE),
                function (el) { return !el.hidden && el.offsetParent !== null; }
            );
        }
        function trap(e) {
            if (e.key !== 'Tab' && e.keyCode !== 9) return;
            var f = focusables();
            if (!f.length) { e.preventDefault(); dialog.focus(); return; }
            var first = f[0];
            var last  = f[f.length - 1];
            if (e.shiftKey && document.activeElement === first) {
                e.preventDefault();
                last.focus();
            } else if (!e.shiftKey && document.activeElement === last) {
                e.preventDefault();
                first.focus();
            }
        }

        // --- open / close --------------------------------------------
        function open() {
            lastFocused = document.activeElement;
            root.hidden = false;
            root.classList.add('is-open');
            document.documentElement.classList.add('kudo-popup-lock');
            document.addEventListener('keydown', trap, true);
            dialog.focus();
        }
        function close() {
            document.removeEventListener('keydown', trap, true);
            root.classList.remove('is-open');
            root.hidden = true;
            document.documentElement.classList.remove('kudo-popup-lock');
            if (lastFocused && typeof lastFocused.focus === 'function') {
                lastFocused.focus();
            }
        }
        // Closes only via the close button / dismiss link — not the overlay,
        // not Esc (see [data-kudo-popup-dismiss] in the template).
        function dismiss() {
            if (!preview) hideFor(days * DAY);
            close();
        }
        function markSubmitted() {
            if (!preview) hideFor(3650 * DAY);
        }

        Array.prototype.forEach.call(
            root.querySelectorAll('[data-kudo-popup-dismiss]'),
            function (el) { el.addEventListener('click', dismiss); }
        );

        // --- form -------------------------------------------------
        var form      = root.querySelector('[data-kudo-popup-form]');
        var errorEl   = root.querySelector('[data-kudo-popup-error]');
        var successEl = root.querySelector('[data-kudo-popup-success]');
        var EMAIL_RE  = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

        function showError(msg) {
            if (!errorEl) return;
            errorEl.textContent = msg;
            errorEl.hidden = false;
        }

        if (form) {
            form.addEventListener('submit', function (e) {
                e.preventDefault();
                if (errorEl) errorEl.hidden = true;

                var input = form.querySelector('input[name="email"]');
                var email = (input && input.value || '').trim();
                if (!EMAIL_RE.test(email)) {
                    showError(cfg.i18nInvalid || 'Please enter a valid email address.');
                    return;
                }

                var btn = form.querySelector('button[type="submit"]');
                if (btn) btn.disabled = true;

                console.log('[kudo-popup] submitting email:', email); // debug — remove when done

                var body = new URLSearchParams();
                body.set('action', cfg.action);
                body.set('nonce', cfg.nonce);
                body.set('popup_id', cfg.id);
                body.set('email', email);

                fetch(cfg.ajaxUrl, { method: 'POST', credentials: 'same-origin', body: body })
                    .then(function (r) { return r.json(); })
                    .then(function (res) {
                        console.log('[kudo-popup] server response:', res); // debug — remove when done
                        if (res && res.success) {
                            markSubmitted();
                            // Swap the input / submit / "no thanks" for the
                            // success message; keep the privacy note.
                            form.hidden = true;
                            hide('.kudo-popup__dismiss-link');
                            if (successEl) successEl.hidden = false;
                            var closeMs = parseInt(cfg.successCloseMs, 10);
                            if (closeMs > 0) window.setTimeout(close, closeMs);
                        } else {
                            showError((res && res.data && res.data.message) || cfg.i18nError || 'Something went wrong.');
                            if (btn) btn.disabled = false;
                        }
                    })
                    .catch(function () {
                        showError(cfg.i18nError || 'Something went wrong. Please try again.');
                        if (btn) btn.disabled = false;
                    });
            });
        }

        function hide(sel) {
            var el = root.querySelector(sel);
            if (el) el.hidden = true;
        }

        // --- trigger ---------------------------------------------
        // Preview still uses the popup's configured delay (not instant) —
        // it only bypasses the frequency cap and never persists state.
        window.setTimeout(open, Math.max(0, (parseInt(cfg.delay, 10) || 0)) * 1000);
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }
})();
